import { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Modal,
    Typography,
    Fade,
    Backdrop,
    CircularProgress
} from "@mui/material";
import { toast } from "react-toastify";
import { updateSubscriptionPlanDetail } from "../../../service/subscriptionService";
import { SubscriptionPlan } from "../../../types/subscriptionTypes";

interface EditSubscriptionPlanProps {
    open: boolean;
    onClose: () => void;
    plan: SubscriptionPlan | null;
    refreshPlans: () => void;
}

const EditSubscriptionPlan: React.FC<EditSubscriptionPlanProps> = ({ open, onClose, plan, refreshPlans }) => {
    const [formData, setFormData] = useState({
        title: "",
        descriptions: "",
        price: "",
        duration: "",
        storage_size: "",
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (plan) {
            setFormData({
                title: plan.title,
                descriptions: plan.descriptions || "",
                price: plan.price.toString(),
                duration: plan.duration.toString(),
                storage_size: plan.storage_size.toString(),
            });
        }
    }, [plan]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async () => {
        if (!plan) return;
        setLoading(true);
        try {
            await updateSubscriptionPlanDetail(plan.id, {
                title: formData.title,
                descriptions: formData.descriptions,
                price: formData.price,
                duration: parseFloat(formData.duration),
                storage_size: parseInt(formData.storage_size),
            });

            toast.success("Subscription plan updated successfully!");
            refreshPlans();
            onClose();
        } catch (error) {
            toast.error("Failed to update subscription plan.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
            <Fade in={open}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" mb={2}>
                        Edit Subscription Plan
                    </Typography>
                    <TextField label="Title" name="title" fullWidth margin="dense" value={formData.title} onChange={handleChange} />
                    <TextField label="Description" name="descriptions" fullWidth margin="dense" value={formData.descriptions} onChange={handleChange} />
                    <TextField label="Price" name="price" type="number" fullWidth margin="dense" value={formData.price} onChange={handleChange} />
                    <TextField label="Duration (years)" name="duration" type="number" fullWidth margin="dense" value={formData.duration} onChange={handleChange} />
                    <TextField label="Storage Size (KB)" name="storage_size" type="number" fullWidth margin="dense" value={formData.storage_size} onChange={handleChange} />

                    <Button variant="contained" color="primary" fullWidth sx={{ mt: 2 }} onClick={handleSubmit} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : "Update Plan"}
                    </Button>
                </Box>
            </Fade>
        </Modal>
    );
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 3,
};

export default EditSubscriptionPlan;
